<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import {
  adminDeleteContract,
  adminListContracts,
  adminListDistricts,
  adminListEngineers,
  adminPatchContract,
  type ContractAdmin,
  type Engineer,
} from '../api';

// TASK-030: удаление договора — только админ (менеджер видит таб, но не удаляет).
const props = withDefaults(defineProps<{ isAdmin?: boolean }>(), { isAdmin: false });

const contracts = ref<ContractAdmin[]>([]);
const engineers = ref<Engineer[]>([]);
const districts = ref<string[]>([]);

const loading = ref(false);
const error = ref<string | null>(null);
const search = ref('');

// Фильтры
const filterStatus = ref<'all' | 'claimed' | 'assigned'>('all');
const filterDateFrom = ref<string>('');
const filterDateTo = ref<string>('');
const filterEngineer = ref<string>('');
// TASK-021
const filterB24 = ref<'all' | 'entered' | 'missing'>('all');

// Edit modal state
const editing = ref<ContractAdmin | null>(null);
const editClient = ref('');
const editDate = ref('');
const editDistrict = ref('');
const editCity = ref('');  // TASK-027: город договора
const editEngineerId = ref('');
// TASK-020: «для кого» взят
const editBeneficiaryId = ref('');
// TASK-021: Б24
const editB24DealId = ref('');
const editB24Entered = ref(false);

async function load() {
  loading.value = true;
  error.value = null;
  try {
    const filters: Record<string, string> = {};
    if (filterStatus.value !== 'all') filters.status = filterStatus.value;
    if (filterDateFrom.value) filters.date_from = filterDateFrom.value;
    if (filterDateTo.value) filters.date_to = filterDateTo.value;
    if (filterEngineer.value) filters.engineer_id = filterEngineer.value;
    if (filterB24.value !== 'all') filters.b24 = filterB24.value;

    const [c, e, d] = await Promise.all([
      adminListContracts(filters),
      engineers.value.length ? Promise.resolve(engineers.value) : adminListEngineers(),
      districts.value.length ? Promise.resolve(districts.value) : adminListDistricts(),
    ]);
    contracts.value = c;
    engineers.value = e;
    districts.value = d;
  } catch (err) {
    error.value = err instanceof Error ? err.message : String(err);
  } finally {
    loading.value = false;
  }
}

onMounted(load);

const filtered = computed(() => {
  if (!search.value.trim()) return contracts.value;
  const q = search.value.toLowerCase();
  return contracts.value.filter((c) => {
    return (
      String(c.number).includes(q) ||
      (c.client_name?.toLowerCase().includes(q) ?? false) ||
      c.engineer_name.toLowerCase().includes(q)
    );
  });
});

function startEdit(c: ContractAdmin) {
  editing.value = c;
  editClient.value = c.client_name ?? '';
  editDate.value = c.date;
  editDistrict.value = c.district ?? '';
  editCity.value = c.contract_city ?? '';
  editEngineerId.value = c.engineer_id;
  editBeneficiaryId.value = c.beneficiary_engineer_id ?? '';
  editB24DealId.value = c.b24_deal_id ?? '';
  editB24Entered.value = c.b24_entered_at != null;
}

function cancelEdit() {
  editing.value = null;
}

async function submitEdit() {
  if (!editing.value) return;
  const original = editing.value;
  const payload: Record<string, string | boolean> = {};
  if (editClient.value.trim() && editClient.value.trim() !== original.client_name) {
    payload.client_name = editClient.value.trim();
  }
  if (editDate.value && editDate.value !== original.date) {
    payload.date = editDate.value;
  }
  if (editDistrict.value && editDistrict.value !== (original.district ?? '')) {
    payload.district = editDistrict.value;
  }
  // TASK-027: город договора (пустая строка = снять — отправляем '' явно).
  if (editCity.value.trim() !== (original.contract_city ?? '')) {
    payload.contract_city = editCity.value.trim();
  }
  if (editEngineerId.value && editEngineerId.value !== original.engineer_id) {
    payload.engineer_id = editEngineerId.value;
  }
  // TASK-020: beneficiary
  const origBenef = original.beneficiary_engineer_id ?? '';
  if (editBeneficiaryId.value !== origBenef) {
    if (editBeneficiaryId.value) {
      payload.beneficiary_engineer_id = editBeneficiaryId.value;
    } else {
      payload.clear_beneficiary = true;
    }
  }
  // TASK-021: Б24
  const origB24 = original.b24_deal_id ?? '';
  const origB24Entered = original.b24_entered_at != null;
  if (editB24Entered.value !== origB24Entered) {
    if (editB24Entered.value) {
      payload.b24_deal_id = editB24DealId.value.trim();  // может быть пусто
    } else {
      payload.clear_b24 = true;
    }
  } else if (editB24Entered.value && editB24DealId.value.trim() !== origB24) {
    // Уже отмечен, но deal_id поменялся
    payload.b24_deal_id = editB24DealId.value.trim();
  }
  if (Object.keys(payload).length === 0) {
    editing.value = null;
    return;
  }
  try {
    await adminPatchContract(original.number, payload as unknown as Parameters<typeof adminPatchContract>[1]);
    editing.value = null;
    await load();
  } catch (err) {
    error.value = err instanceof Error ? err.message : String(err);
  }
}

// TASK-030: удаление договора. Со сканом → полное удаление + PDF; без → отмена.
async function deleteContract(c: ContractAdmin) {
  const hasScan = Boolean(c.scan_url);
  const kind = hasScan ? 'ПОЛНОСТЬЮ удалить' : 'отменить';
  const warn = hasScan
    ? `Договор № ${c.number} (${c.client_name ?? 'без ФИО'}) будет удалён ПОЛНОСТЬЮ ` +
      `вместе со сканом на Я.Диске. Номер вернётся в оборот. Действие необратимо.`
    : `Номер № ${c.number} будет отменён (скана нет). Номер вернётся в оборот.`;
  if (!confirm(`${kind} № ${c.number}?\n\n${warn}`)) return;
  const reason = prompt('Причина удаления (необязательно):') ?? undefined;
  try {
    const res = await adminDeleteContract(c.number, reason || undefined);
    error.value = null;
    if (editing.value?.number === c.number) editing.value = null;
    await load();
    // Лёгкое подтверждение через тот же error-баннер (перекрасить бы, но ок).
    console.info(`Договор ${res.number} удалён (скан: ${res.had_scan})`);
  } catch (err) {
    error.value = err instanceof Error ? err.message : String(err);
  }
}

function statusBadge(s: string): string {
  return s === 'assigned' ? '✓' : '◷';
}

function ocrBadge(o: string | null | undefined): string {
  if (o === 'confirmed') return '✓';
  if (o === 'unconfirmed') return '⚠';
  return '';
}

function b24Badge(c: ContractAdmin): string {
  // TASK-021 шильдик в списке: 📋 если внесён, ⏳ если ASSIGNED но не внесён,
  // пусто если ещё claimed.
  if (c.b24_entered_at) return '📋';
  if (c.status === 'assigned') return '⏳';
  return '';
}
</script>

<template>
  <section class="flex flex-col gap-3 p-2 admin-tab-content">
    <header>
      <h2 class="text-lg font-semibold">Договоры</h2>
    </header>

    <!-- Filters -->
    <div class="grid grid-cols-2 gap-2">
      <select v-model="filterStatus" @change="load" class="input">
        <option value="all">Все статусы</option>
        <option value="claimed">🕒 В работе</option>
        <option value="assigned">✅ Завершён</option>
      </select>
      <select v-model="filterEngineer" @change="load" class="input">
        <option value="">Все исполнители</option>
        <option v-for="e in engineers" :key="e.max_user_id" :value="e.max_user_id">
          {{ e.full_name_override || e.max_user_id }} ({{ e.role }})
        </option>
      </select>
      <input v-model="filterDateFrom" type="date" class="input" @change="load" />
      <input v-model="filterDateTo" type="date" class="input" @change="load" />
      <select v-model="filterB24" @change="load" class="input col-span-2">
        <option value="all">Б24: все</option>
        <option value="missing">⏳ Не внесены в Б24 (завершённые)</option>
        <option value="entered">📋 Внесены в Б24</option>
      </select>
    </div>

    <input
      v-model="search"
      type="text"
      placeholder="Поиск по номеру / ФИО клиента / выездному"
      class="input"
    />

    <p v-if="error" class="text-red-500 text-sm">{{ error }}</p>
    <p v-if="loading" class="text-hint text-sm">Загрузка…</p>

    <!-- List -->
    <ul class="flex flex-col gap-2">
      <li
        v-for="c in filtered"
        :key="c.number"
        class="border rounded p-2 flex flex-col gap-1"
      >
        <div class="flex justify-between items-center">
          <span class="font-mono">
            № {{ c.number }} от {{ c.date }} {{ statusBadge(c.status) }} {{ ocrBadge(c.ocr_verified) }} {{ b24Badge(c) }}
          </span>
          <button class="text-sm text-blue-600" @click="startEdit(c)">
            Изменить
          </button>
        </div>
        <p class="text-sm">{{ c.client_name || '— нет ФИО клиента —' }}</p>
        <p class="text-xs text-hint">
          {{ c.engineer_name }}
          <span v-if="c.beneficiary_engineer_name" class="text-purple-600">
            (для {{ c.beneficiary_engineer_name }})
          </span>
          · {{ c.district || 'без района' }}
          <span v-if="c.contract_city" class="text-emerald-600">
            · 🏙 {{ c.contract_city }}
          </span>
          <span v-if="c.scan_url"> · скан есть</span>
          <span v-else class="text-orange-500"> · скан не прислан</span>
        </p>
      </li>
      <li v-if="!loading && filtered.length === 0" class="text-hint text-sm text-center">
        Нет договоров по фильтру
      </li>
    </ul>

    <!-- Edit modal -->
    <div
      v-if="editing"
      class="fixed inset-0 bg-black/50 flex items-end z-50"
      @click.self="cancelEdit"
    >
      <div class="bg-white w-full p-4 rounded-t-xl flex flex-col gap-3 max-h-[90vh] overflow-y-auto">
        <h3 class="text-lg font-semibold">Изменить № {{ editing.number }}</h3>

        <label class="text-xs text-hint">ФИО клиента</label>
        <input v-model="editClient" type="text" class="input" />

        <label class="text-xs text-hint">Дата договора</label>
        <input v-model="editDate" type="date" class="input" />

        <label class="text-xs text-hint">Район (закрепление сотрудника)</label>
        <select v-model="editDistrict" class="input">
          <option value="">— без района —</option>
          <option v-for="d in districts" :key="d" :value="d">{{ d }}</option>
        </select>

        <label class="text-xs text-hint">
          Город договора (где фактически заключён)
          <span v-if="editing?.contract_city_source" class="text-blue-500">
            · источник: {{ editing.contract_city_source }}
          </span>
        </label>
        <input
          v-model="editCity"
          type="text"
          class="input"
          placeholder="напр. Симферополь — пусто = снять"
        />

        <label class="text-xs text-hint">Исполнитель (max_user_id)</label>
        <select v-model="editEngineerId" class="input">
          <option v-for="e in engineers" :key="e.max_user_id" :value="e.max_user_id">
            {{ e.full_name_override || e.max_user_id }} ({{ e.role }})
          </option>
        </select>

        <label class="text-xs text-hint">
          Для кого взят (если взят менеджером для выездного)
        </label>
        <select v-model="editBeneficiaryId" class="input">
          <option value="">— для себя —</option>
          <option v-for="e in engineers" :key="e.max_user_id" :value="e.max_user_id">
            {{ e.full_name_override || e.max_user_id }} ({{ e.role }})
          </option>
        </select>

        <!-- TASK-021: Б24 -->
        <div class="border-t pt-3 mt-2">
          <label class="flex items-center gap-2 text-sm font-medium">
            <input type="checkbox" v-model="editB24Entered" />
            📋 Внесён в Битрикс24
          </label>
          <input
            v-if="editB24Entered"
            v-model="editB24DealId"
            type="text"
            inputmode="numeric"
            placeholder="ID сделки Б24 (необязательно)"
            class="input mt-2"
          />
        </div>

        <div class="flex gap-2 mt-2">
          <button class="btn flex-1" @click="submitEdit">Сохранить</button>
          <button class="btn btn-secondary flex-1" @click="cancelEdit">
            Отмена
          </button>
        </div>

        <!-- TASK-030: удаление договора — только админ -->
        <button
          v-if="props.isAdmin"
          class="btn w-full mt-2 bg-red-600 text-white hover:bg-red-700"
          @click="deleteContract(editing)"
        >
          🗑 {{ editing.scan_url ? 'Удалить договор полностью' : 'Отменить номер' }}
        </button>
      </div>
    </div>
  </section>
</template>
